/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { usePlaySound } from '@hook/common/useSound';
import { EStudyConfigurationMode } from '@datatype/iyagigo/type';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';

interface IProps {
  goal: number;
  type: EStudyConfigurationMode;
  suffix: '개' | '회';
  isEditable?: boolean;
}

// ############### variable ###############

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const CountContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
`;

const EditContainer = styled.div<{ $isAuthenticated: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 10px;
  cursor: ${(props) => (props.$isAuthenticated ? 'pointer' : 'default')};
`;

const AssignmentControl: React.FC<IProps> = ({ goal, type, suffix, isEditable = true }) => {
  // ############### state ###############
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const dispatch = useTypedDispatch();
  const playSound = usePlaySound();
  const subscriptionQuery = useMySubscriptionQuery();
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  // ############### handler ###############
  const handleEditClick = () => {
    // 학습 설정은 Premium 전용
    // 비회원/무료회원 모두 subscription-wall (access) — 구독 페이지에서 비회원이면 가입 유도
    if (!isSubscriber) {
      dispatch(
        modalActions.showWallToast({
          type: 'subscription-wall',
          data: { trigger: 'access' },
        }),
      );
      return;
    }

    dispatch(modalActions.showDialogModal({ type: 'study-configuration', data: { mode: type } }));
  };

  return (
    <Wrapper>
      <TextBlock
        text={'일일 목표'}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: true },
          box: { alignX: 'left' },
        }}
      />
      <EditContainer $isAuthenticated={!!isAuthenticated} onClick={handleEditClick}>
        {/*<IconBlock icon={'left'} design={{ icon: { size: 'medium', kind: 'filled', color: color.BLACK_2 } }} />*/}
        <CountContainer>
          <TextBlock
            text={`${goal}`}
            design={{
              text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
              box: { alignX: 'left' },
            }}
          />
          <TextBlock
            text={suffix}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: false },
              box: { alignX: 'left' },
            }}
          />
        </CountContainer>
        {isEditable && (
          <IconBlock
            icon={'edit'}
            design={{
              icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
            }}
          />
        )}
      </EditContainer>
    </Wrapper>
  );
};

export default AssignmentControl;
